// client side template for the career page

import React, { useState, useEffect } from 'react'
import { graphql, useStaticQuery, navigate } from 'gatsby'
import slugify from 'slugify'
import { useTraffit } from '../../../hooks/useTraffit'
import { useCareer } from 'hooks/graphql/page'
import { SEO } from 'components/Seo'
import { NavLinkButton } from '../../../components/Link'
import CareerHero from './CareerHero'
import { addBlankToUrls } from '../../../utils/html'
import { POSITION_PLACEHOLDER } from '../constants'
import { extractSalaryFromBenefits } from '../../../components/sections/JobOffers/utils/jobUtils'
import IconLoading from '../../../components/icons/IconLoading'
import AnnouncementTopBar from '../../../components/AnnouncementTopBar'
import { getFirstPath } from 'utils/link'
import { StyledCareerAdWrapper } from './StyledCareerAdWrapper'

const CareerPage = ({ jobSlug, location }) => {
  const { jobPosts } = useTraffit()
  const [jobData, setJobData] = useState(null)
  const {
    careerDictionary: { applyLabel },
    careerAdPage: { seoDescription },
  } = useCareer()

  const data = useStaticQuery(graphql`
    query CareerDetailQuery {
      sanityCommonSections(language: { eq: "en" }) {
        announcementTopBar {
          isAnnouncementTopBarDisplayed
          _rawAnnouncementTopBarShort
          _rawAnnouncementTopBarLong
          slugsList
        }
      }
    }
  `)

  useEffect(() => {
    if (jobPosts.length > 0 && jobSlug) {
      const job = jobPosts.find((job) => {
        const jobSlugGenerated = `${slugify(job.name, { lower: true })}${
          job._country ? `-${slugify(job._country, { lower: true })}` : ''
        }`
        return jobSlugGenerated === jobSlug
      })

      if (job) {
        setJobData(job)
      } else {
        navigate('/careers')
        return
      }
    }
  }, [jobPosts, jobSlug])

  const {
    announcementTopBar: {
      isAnnouncementTopBarDisplayed,
      _rawAnnouncementTopBarLong,
      _rawAnnouncementTopBarShort,
      slugsList,
    },
  } = data.sanityCommonSections

  const announcementTopBarData = {
    isDisplayed: isAnnouncementTopBarDisplayed,
    longAnnouncement: _rawAnnouncementTopBarLong,
    shortAnnouncement: _rawAnnouncementTopBarShort,
  }

  const isAnnouncementForCareers = slugsList?.includes('careers')

  if (!jobData) {
    return (
      <>
        <SEO title="Loading Job..." description="Loading job details..." location={location} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '2rem',
            margin: '8rem auto',
          }}
        >
          <IconLoading />
        </div>
      </>
    )
  }

  const {
    _description: description,
    _requirements: requirements,
    _benefits: benefits,
    name: title,
    applicationFormLink,
    advertDescription,
    created_at,
    job_type: employment_type,
  } = jobData

  const jobLocation = {
    country: jobData?._country,
    city: jobData?._city,
    telecommuting: jobData?._telecommuting,
    overrideLocationLabel: jobData?._override_location_label,
  }

  const indexOfNetBenefits = extractSalaryFromBenefits(benefits)?.indexOf('net')
  const salaryRange = extractSalaryFromBenefits(benefits)?.slice(4, indexOfNetBenefits)
  const twoMontsAdvancedCreatedAtDate = new Date(
    new Date(created_at).setMonth(new Date(created_at).getMonth() + 2),
  ).toDateString()
  const indexOfDotDescription = description?.indexOf('.')
  const extractedDescription = description?.slice(3, indexOfDotDescription)

  return (
    <>
      <SEO
        title={title}
        description={seoDescription.replace(POSITION_PLACEHOLDER, title)}
        image={{ asset: { url: 'https://10clouds.com/images/hiring-seo-image.jpg' } }}
        location={location}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'JobPosting',
            title: title,
            description: `<p>${extractedDescription}</p>`,
            identifier: {
              '@type': 'PropertyValue',
              name: '10Clouds',
              value: jobSlug,
            },
            datePosted: created_at,
            validThrough: twoMontsAdvancedCreatedAtDate,
            applicantLocationRequirements: {
              '@type': 'Country',
              name: jobLocation.country,
            },
            jobLocationType: jobLocation.telecommuting ? 'TELECOMMUTE' : 'ONSITE',
            employmentType: employment_type,
            hiringOrganization: {
              '@type': 'Organization',
              name: '10Clouds',
              sameAs: 'http://10clouds.com',
              logo: 'https://10clouds.com//favicon-32x32.png?v=49a3c48f999c570021459142bc30db90',
            },
            baseSalary: {
              '@type': 'MonetaryAmount',
              currency: 'PLN',
              value: {
                '@type': 'QuantitativeValue',
                value: salaryRange,
                unitText: 'MONTH',
              },
            },
          })}
        </script>
      </SEO>
      {isAnnouncementForCareers && (
        <AnnouncementTopBar
          announcementData={announcementTopBarData}
          pageName={getFirstPath('/careers')}
        />
      )}

      <CareerHero title={title} location={jobLocation} />

      <StyledCareerAdWrapper>
        <div className="career-ad__content">
          {advertDescription ? (
            <div dangerouslySetInnerHTML={{ __html: addBlankToUrls(advertDescription) }}></div>
          ) : (
            <>
              {description && (
                <div dangerouslySetInnerHTML={{ __html: addBlankToUrls(description) }}></div>
              )}
              {requirements && (
                <div dangerouslySetInnerHTML={{ __html: addBlankToUrls(requirements) }}></div>
              )}
              {benefits && (
                <div dangerouslySetInnerHTML={{ __html: addBlankToUrls(benefits) }}></div>
              )}
            </>
          )}
          <NavLinkButton to={applicationFormLink} variant="filled">
            {applyLabel}
          </NavLinkButton>
        </div>
      </StyledCareerAdWrapper>
    </>
  )
}

export default CareerPage
